import { InjectionToken, Type } from '@angular/core';
import { Observable, ReplaySubject, filter, map } from 'rxjs';

/** `sm` 420px, `md` 640px, `lg` 900px, or any CSS width. */
export type DialogWidth = 'sm' | 'md' | 'lg' | (string & Record<never, never>);

export interface DialogOptions<D = unknown> {
  /** Translation key or plain text. */
  title: string;
  /** Handed to the content component through `DIALOG_DATA`. */
  data?: D;
  width?: DialogWidth;
  /** Shows the close button and lets Esc close the dialog. Default `true`. */
  closable?: boolean;
  /** A click on the mask closes the dialog. Default `false`. */
  dismissableMask?: boolean;
}

export interface ConfirmOptions {
  /** Translation key or plain text. */
  title: string;
  /** Translation key or plain text. */
  message: string;
  /** Translation key or plain text. Default `shared.terms.confirm`. */
  confirmLabel?: string;
  /** Translation key or plain text. Default `shared.terms.cancel`. */
  cancelLabel?: string;
  /** `danger` styles the confirm button as destructive. Default `primary`. */
  severity?: 'primary' | 'danger';
}

export interface AlertOptions {
  /** Translation key or plain text. */
  title: string;
  /** Translation key or plain text. */
  message: string;
  /** Translation key or plain text. Default `shared.terms.ok`. */
  okLabel?: string;
  /**
   * Shows the OK button. Default `true`. Without it (and with
   * `closable: false`) the dialog stays until the page navigates away: for
   * a notice that the app itself acts on, such as "logging out...".
   */
  showOk?: boolean;
  /** Shows the close button and lets Esc close the dialog. Default `true`. */
  closable?: boolean;
}

/** What a content component opened through `DialogService.open` injects. */
export const DIALOG_DATA = new InjectionToken<unknown>('DIALOG_DATA');

/** How a dialog was closed. `dismiss`: the X button, Esc or the mask. */
export type DialogAction = 'ok' | 'cancel' | 'dismiss';

/** What `DialogRef.afterClosed` emits. `data` is set only for `ok`. */
export interface DialogResult<R> {
  action: DialogAction;
  data?: R;
}

/**
 * What a dialog content component implements, so `DialogService.open` can
 * infer the data it takes (`D`) and the result it returns on ok (`R`):
 *
 * ```ts
 * class RenameDialog implements DialogContent<{ name: string }, string> {
 *   readonly data = inject<{ name: string }>(DIALOG_DATA);
 *   readonly dialogRef = inject<DialogRef<string>>(DialogRef);
 * }
 * ```
 */
export interface DialogContent<D = void, R = void> {
  readonly data: D;
  readonly dialogRef: DialogRef<R>;
}

/** The data type a content component takes. */
export type DialogDataOf<C> = C extends DialogContent<infer D, any> ? D : never;

/** The type a content component returns with `ok`. */
export type DialogResultOf<C> = C extends DialogContent<any, infer R>
  ? R
  : never;

/**
 * The options of `DialogService.open` for a content component: `data` is
 * absent (or optional) when the component takes none, required and typed
 * otherwise.
 */
export type DialogOpenOptions<D> = Omit<DialogOptions, 'data'> &
  ([D] extends [void] ? { data?: undefined } : { data: D });

/**
 * Handle on an open dialog, for the content component (`ok`, `cancel`) and
 * the opener (`afterClosed`, `afterOk`). Content components inject it too.
 */
export class DialogRef<R = void> {
  private readonly _closed = new ReplaySubject<DialogResult<R>>(1);
  private _done = false;
  private _closeImpl: (result: DialogResult<R>) => void = () => undefined;

  /**
   * Emits exactly once, then completes: how the dialog was closed and, for
   * `ok`, its data. The X button, Esc and the mask give `dismiss`.
   */
  readonly afterClosed: Observable<DialogResult<R>> =
    this._closed.asObservable();

  /**
   * Emits the data when the dialog closed with `ok`, then completes;
   * completes without emitting otherwise.
   */
  readonly afterOk: Observable<R> = this._closed.pipe(
    filter((result) => result.action === 'ok'),
    map((result) => result.data as R)
  );

  /** Closes the dialog, confirming with `data`. */
  ok(data: R): void {
    this._closeImpl({ action: 'ok', data });
  }

  /** Closes the dialog without a result. */
  cancel(): void {
    this._closeImpl({ action: 'cancel' });
  }

  /** @internal Closes as `dismiss` (the frame's X button). */
  _dismiss(): void {
    this._closeImpl({ action: 'dismiss' });
  }

  /** @internal Wired up by `DialogService`. */
  _attach(closeImpl: (result: DialogResult<R>) => void): void {
    this._closeImpl = closeImpl;
  }

  /** @internal Called by `DialogService` when the dialog has closed. */
  _complete(result: DialogResult<R>): void {
    if (this._done) return;
    this._done = true;
    this._closed.next(result);
    this._closed.complete();
  }
}

/** @internal What the frame component receives from `DialogService`. */
export interface DialogFrameData {
  component: Type<unknown>;
  title: string;
  data: unknown;
  closable: boolean;
  ref: DialogRef<any>;
}
